import { useState, type FormEvent } from 'react';
import '@missions/ui/styles.css';
import { LAST_SLUG_KEY } from '../lib/tenant';

/** Root of the temp host (missions.devninjaweb.com/) with no workspace in the path: ask for it. */
/** `next`: the path to open inside the workspace (e.g. /reset/<token> from a link that lost its slug). */
export function FindWorkspace({ next = '/' }: { next?: string }) {
  const [slug, setSlug] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const clean = slug.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^missions\.devninjaweb\.com\/?/, '').split('/')[0] ?? '';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^[a-z0-9-]{3,40}$/.test(clean)) {
      setErr('Use the workspace name from your invite link (letters, numbers and dashes).');
      return;
    }
    window.location.assign(`/${clean}${next.startsWith('/') ? next : '/'}`);
  };

  return (
    <main className="ws-404 ws-find">
      <img src="/brand/missions-logo.png" alt="Missions" height={28} />
      <h1>Go to your workspace</h1>
      <p>
        {next !== '/' ? 'This link is missing its workspace. ' : ''}Enter your workspace name. It’s the part after the slash in the link your admin shared.
      </p>
      <form onSubmit={submit} noValidate>
        <label className="ws-field" aria-invalid={err ? 'true' : undefined}>
          <span className="pre">missions.devninjaweb.com/</span>
          <input
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            aria-label="Workspace name"
            placeholder="your-company"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setErr(null);
            }}
          />
        </label>
        {err ? <div className="ws-err" role="alert">{err}</div> : null}
        <button className="ui-btn primary" type="submit">Continue</button>
      </form>
      <a className="ws-learn" href="https://devninjaweb.com" rel="noopener">Learn about Missions</a>
    </main>
  );
}

/** Where to send someone who opens the bare root: their last workspace on this device, if any. */
export function lastWorkspace(): string | null {
  try {
    const s = localStorage.getItem(LAST_SLUG_KEY);
    return s && /^[a-z0-9-]{3,40}$/.test(s) ? s : null;
  } catch {
    return null;
  }
}
